import React, { useCallback, useEffect, useRef, useState } from "react";
import { TextInputType } from "../../../../Forms/Inputs/TextInput/React";
import {
  Button,
  ButtonColor,
  ButtonSize,
  ButtonType
} from "../../../../Shared/Buttons/React";
import { OtpInput } from "../../components/fields/otpInput";
import { PhoneNumberField } from "../../components/fields/phoneNumberField";
import { ValidatedTextInputField } from "../../components/fields/validatedTextInputField";
import { ErrorMessage } from "../../components/shared/errorMessage";
import { useProvetContext, useTranslationContext } from "../../context";
import { ProvetBookingFlowActionType } from "../../hooks/provetBookingFlowReducer";
import { ProvetValidationUtilities } from "../../utilities";
import { ApiError } from "../../utilities/apiClient";
import { useContactDetailsLocalStorage } from "../provetContactDetails/hooks/useContactDetailsLocalStorage";
import { usePhoneVerification } from "./hooks/usePhoneVerification";

export type VerificationStep = "input" | "verify" | "loading";

export interface ProvetPhoneVerificationProps {
  /** Prelude SDK key for fraud detection signals */
  preludeSdkKey?: string;
  /** Callback when verification is successfully completed */
  onVerificationComplete: (requestId: string) => void;
  /** Callback when internal step changes (for updating parent title) */
  onStepChange?: (step: VerificationStep) => void;
  /** Callback when loading state changes */
  onLoadingChange?: (isLoading: boolean) => void;
}

const OTP_INPUT_LENGTH = 6;
const OTP_EXPIRY_SECONDS = 10 * 60; // Twilio default is 10 minutes

/**
 * The site's inline link style (dark), as rendered by `<ani:inline-link>`
 * (`Features/Shared/Text/InlineLinkTagHelper.cs`), for the buttons that look like links.
 * While disabled they lose the underline and are muted.
 */
const INLINE_LINK_BUTTON_CLASSES =
  "relative inline-block font-t-bold text-dark-blue transition-short-in-out " +
  "underline underline-offset-6 decoration-turquoise-800 hover:decoration-current " +
  "focus-visible:decoration-transparent focus-outline " +
  "disabled:no-underline disabled:opacity-50 disabled:cursor-not-allowed";

/**
 * Phone Verification Component
 *
 * Two-step phone verification process:
 * 1. Enter email and phone number, then request verification code
 * 2. Enter received OTP code to verify
 *
 * Features:
 * - Email input with validation
 * - Phone number input with country code selector
 * - 6-digit OTP input with individual boxes
 * - 10-minute countdown timer; when it runs out the code can no longer be verified
 * - Change phone number from the code input
 * - Resend code functionality
 * - Loading states and error handling
 */
export const ProvetPhoneVerification: React.FC<ProvetPhoneVerificationProps> = ({
  preludeSdkKey,
  onVerificationComplete,
  onStepChange,
  onLoadingChange
}) => {
  const provetContext = useProvetContext();
  const translationContext = useTranslationContext();
  const { loadContactDetails } = useContactDetailsLocalStorage();

  const [verificationStep, setVerificationStep] = useState<VerificationStep>("input");

  // Read email and phone from form state (single source of truth)
  const email =
    provetContext.bookingFlowState.formData?.contactDetails?.client?.email || "";
  const phoneNumber =
    provetContext.bookingFlowState.formData?.contactDetails?.client?.phoneNumber || "";
  const [otpCode, setOtpCode] = useState("");
  const [timeRemaining, setTimeRemaining] = useState(OTP_EXPIRY_SECONDS);
  // When the code that was sent expires (epoch ms). The countdown is derived from this, so it
  // stays correct even when the browser slows down timers (eg in a background tab).
  const [codeExpiresAt, setCodeExpiresAt] = useState<number | null>(null);
  const [hasBlurredEmail, setHasBlurredEmail] = useState(false);
  const [hasBlurredPhone, setHasBlurredPhone] = useState(false);
  const [isValidEmail, setIsValidEmail] = useState(false);
  const [isValidPhone, setIsValidPhone] = useState(false);
  const [dispatchId, setDispatchId] = useState<string | undefined>(undefined);
  const [isCheckingUser, setIsCheckingUser] = useState(false);
  const [checkUserError, setCheckUserError] = useState<Error | null>(null);
  // Set when the user goes back from the code input, to focus the phone number field
  const focusPhoneOnInputRef = useRef(false);

  const {
    sendVerificationCode,
    verifyCode,
    resendCode,
    resetVerification,
    isLoading,
    error,
    requestId,
    canResend,
    resendCooldown
  } = usePhoneVerification({
    api: provetContext.api,
    resendDelaySeconds: 10
  });

  // Load remembered email/phoneNumber from localStorage on mount for returning users
  useEffect(() => {
    const remembered = loadContactDetails();
    if (!remembered?.client) {
      return;
    }

    const currentClient = provetContext.bookingFlowState.formData?.contactDetails?.client;

    // Only restore values that aren't already set (don't overwrite existing verified values)
    const shouldRestoreEmail = remembered.client.email && !currentClient?.email;
    const shouldRestorePhone =
      remembered.client.phoneNumber && !currentClient?.phoneNumber;

    if (shouldRestoreEmail || shouldRestorePhone) {
      provetContext.bookingFlowDispatch({
        type: ProvetBookingFlowActionType.SetFormData,
        formData: {
          contactDetails: {
            ...provetContext.bookingFlowState.formData?.contactDetails,
            client: {
              ...currentClient,
              ...(shouldRestoreEmail && { email: remembered.client.email }),
              ...(shouldRestorePhone && { phoneNumber: remembered.client.phoneNumber })
            },
            patient:
              provetContext.bookingFlowState.formData?.contactDetails?.patient || {}
          }
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Call Prelude's dispatchSignals on component mount
  // TODO: Re-add if/when we should switch to Prelude.
  // useEffect(() => {
  //   if (preludeSdkKey && !dispatchId) {
  //     dispatchSignals(preludeSdkKey)
  //       .then(id => {
  //         setDispatchId(id);
  //         console.log("Prelude dispatchSignals called successfully. DispatchId:", id);
  //       })
  //       .catch(err => {
  //         console.error("Failed to call Prelude dispatchSignals:", err);
  //       });
  //   }
  // }, [preludeSdkKey, dispatchId]);

  // Notify parent of loading state changes
  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isLoading);
    }
  }, [isLoading, onLoadingChange]);

  // Validate email
  useEffect(() => {
    setIsValidEmail(ProvetValidationUtilities.isValidEmail(email));
  }, [email]);

  // Validate phone number
  useEffect(() => {
    setIsValidPhone(ProvetValidationUtilities.isValidPhone(phoneNumber));
  }, [phoneNumber]);

  // Notify parent when step changes
  useEffect(() => {
    onStepChange?.(verificationStep);
  }, [verificationStep, onStepChange]);

  // Countdown timer for OTP expiration. The expiry is only detected here, in the browser: the
  // server answers every failed code check the same way, so it doesn't reveal whether a code
  // is wrong, expired, used or out of attempts.
  useEffect(() => {
    if (verificationStep !== "verify" || codeExpiresAt === null) {
      return;
    }

    const updateTimeRemaining = () => {
      const remaining = Math.max(0, Math.ceil((codeExpiresAt - Date.now()) / 1000));
      setTimeRemaining(remaining);
      return remaining;
    };

    if (updateTimeRemaining() === 0) {
      return;
    }

    const intervalId = setInterval(() => {
      if (updateTimeRemaining() === 0) {
        clearInterval(intervalId);
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [verificationStep, codeExpiresAt]);

  const isCodeExpired = timeRemaining <= 0;

  const startCodeTimer = () => {
    setTimeRemaining(OTP_EXPIRY_SECONDS);
    setCodeExpiresAt(Date.now() + OTP_EXPIRY_SECONDS * 1000);
  };

  // Focus the phone number field when the user has gone back to change it
  useEffect(() => {
    if (verificationStep === "input" && focusPhoneOnInputRef.current) {
      focusPhoneOnInputRef.current = false;
      document.getElementById("verification-phone-number")?.focus();
    }
  }, [verificationStep]);

  // Format timer as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Get last 4 digits of phone number
  const getLastDigits = (phone: string): string => {
    const digits = phone.replace(/\D/g, "");
    return digits.slice(-4) || "****";
  };

  const handleEmailChange = useCallback(
    (value: string) => {
      setCheckUserError(null);

      // Update form state directly
      provetContext.bookingFlowDispatch({
        type: ProvetBookingFlowActionType.SetFormData,
        formData: {
          contactDetails: {
            ...provetContext.bookingFlowState.formData?.contactDetails,
            client: {
              ...provetContext.bookingFlowState.formData?.contactDetails?.client,
              email: value
            },
            patient:
              provetContext.bookingFlowState.formData?.contactDetails?.patient || {}
          }
        }
      });
    },
    [
      provetContext.bookingFlowDispatch,
      provetContext.bookingFlowState.formData?.contactDetails
    ]
  );

  const handleEmailBlur = useCallback(() => {
    setHasBlurredEmail(true);
  }, []);

  const handlePhoneNumberChange = useCallback(
    (value: string) => {
      setCheckUserError(null);

      // Update form state directly
      provetContext.bookingFlowDispatch({
        type: ProvetBookingFlowActionType.SetFormData,
        formData: {
          contactDetails: {
            ...provetContext.bookingFlowState.formData?.contactDetails,
            client: {
              ...provetContext.bookingFlowState.formData?.contactDetails?.client,
              phoneNumber: value
            },
            patient:
              provetContext.bookingFlowState.formData?.contactDetails?.patient || {}
          }
        }
      });
    },
    [
      provetContext.bookingFlowDispatch,
      provetContext.bookingFlowState.formData?.contactDetails
    ]
  );

  const handlePhoneNumberBlur = useCallback(() => {
    setHasBlurredPhone(true);
  }, []);

  const handleGetCode = async () => {
    // The resend cooldown also applies after going back to change the phone number
    if (!isValidEmail || !isValidPhone || !canResend) {
      return;
    }

    const sessionId = provetContext.bookingFlowState.formData?.sessionId;

    if (!sessionId) {
      console.error("Session ID is not available");
      return;
    }

    setIsCheckingUser(true);
    setCheckUserError(null);

    try {
      // First, check if user already exists in Provet system
      let userExists: boolean;

      try {
        const userExistsResponse = await provetContext.api.checkUserExists(
          email,
          phoneNumber,
          sessionId
        );
        userExists = userExistsResponse.exists;
      } catch (err) {
        console.error("Failed to check whether the user exists:", err);
        // A rate limit or fault gets the same message as a code that couldn't be sent;
        // network errors and timeouts (no status) keep their own readable message
        setCheckUserError(
          err instanceof ApiError && err.statusCode !== undefined
            ? new Error(
                translationContext?.translations?.errorSendCodeUnavailable ||
                  "We couldn't send a code right now. Please wait a few minutes and try again."
              )
            : err instanceof Error
              ? err
              : new Error(
                  translationContext?.translations?.errorCheckDetailsFailed ||
                    "We couldn't check your details. Please try again."
                )
        );
        return;
      }

      if (userExists) {
        // User exists - skip SMS verification and proceed directly
        // Use a dummy requestId since no actual SMS was sent
        onVerificationComplete("existing-user");
        return;
      }

      // User doesn't exist - proceed with SMS verification
      await sendVerificationCode(phoneNumber, dispatchId);
      setVerificationStep("verify");
      startCodeTimer();
      setOtpCode("");
    } catch (err) {
      // Error is handled by the hook
      console.error("Failed to send verification code:", err);
    } finally {
      setIsCheckingUser(false);
    }
  };

  const handleResendCode = async () => {
    try {
      const codeSent = await resendCode();

      if (codeSent) {
        startCodeTimer();
        setOtpCode("");
      }
    } catch (err) {
      console.error("Failed to resend code:", err);
    }
  };

  const handleChangePhoneNumber = () => {
    resetVerification();
    setOtpCode("");
    setCodeExpiresAt(null);
    setTimeRemaining(OTP_EXPIRY_SECONDS);
    focusPhoneOnInputRef.current = true;
    setVerificationStep("input");
  };

  const handleVerify = async () => {
    if (otpCode.length < 6 || isCodeExpired) {
      return;
    }

    const sessionId = provetContext.bookingFlowState.formData?.sessionId;
    if (!sessionId) {
      console.error("Session ID is not available");
      return;
    }

    try {
      const isValid = await verifyCode(email, phoneNumber, otpCode, sessionId);

      if (isValid && requestId) {
        // Success - notify parent component
        onVerificationComplete(requestId);
      } else {
        // Invalid code - return to verify step
        setVerificationStep("verify");
        setOtpCode("");
      }
    } catch (err) {
      setVerificationStep("verify");
      console.error("Verification failed:", err);
    }
  };

  const handleOtpChange = (value: string) => {
    setOtpCode(value);
  };

  // Phone input view
  if (verificationStep === "input") {
    return (
      <div className="flex flex-col gap-lg">
        {/* Email Field */}
        <ValidatedTextInputField
          id="verification-email"
          name="verification-email"
          inputType={TextInputType.Email}
          label={translationContext?.translations?.email || "Email"}
          description={translationContext?.translations?.emailDescription}
          errorMessage={translationContext?.translations?.emailError}
          placeholder={translationContext?.translations?.emailPlaceholder}
          value={email}
          required={true}
          isValid={isValidEmail}
          hasBlurred={hasBlurredEmail}
          formValidated={false}
          onValueChange={handleEmailChange}
          onBlur={handleEmailBlur}
        />

        {/* Phone Number Field */}
        <PhoneNumberField
          id="verification-phone-number"
          name="verification-phone-number"
          label={
            translationContext?.translations?.provetVerifyPhoneLabel || "Phone number"
          }
          description={
            translationContext?.translations?.provetVerifyPhoneDescription ||
            "We'll send you a verification code"
          }
          errorMessage={translationContext?.translations?.phoneNumberError}
          placeholder={translationContext?.translations?.phoneNumberPlaceholder}
          value={phoneNumber}
          language={provetContext.language}
          required={true}
          isValid={isValidPhone}
          hasBlurred={hasBlurredPhone}
          formValidated={false}
          onValueChange={handlePhoneNumberChange}
          onBlur={handlePhoneNumberBlur}
        />

        {checkUserError && <ErrorMessage error={checkUserError} />}
        {error && <ErrorMessage error={error} />}

        <Button
          onClick={handleGetCode}
          type={ButtonType.Button}
          color={ButtonColor.DarkBlue}
          size={ButtonSize.LG}
          isLoading={isLoading || isCheckingUser}
          disabled={
            !isValidEmail || !isValidPhone || isLoading || isCheckingUser || !canResend
          }
          data-online-booking-action="getVerificationCode"
        >
          {translationContext?.translations?.provetVerifyPhoneButton || "Next"}
          {!canResend && ` (${resendCooldown}s)`}
        </Button>
      </div>
    );
  }

  // OTP verification view
  if (verificationStep === "verify") {
    return (
      <div className="flex flex-col gap-xl">
        <div className="flex flex-col gap-xs">
          <p className="text-t-sm text-t-neutral-700">
            {translationContext?.translations?.provetOtpSubtitle ||
              "Let's make sure you're a real pet owner."}
          </p>

          <div className="flex flex-col">
            <h3 className="text-t-sm font-t-semi-bold text-t-neutral-800">
              {translationContext?.translations?.provetOtpLabel || "Verification code"}
            </h3>

            <p className="text-t-sm text-t-neutral-700">
              {translationContext?.translations?.provetOtpDescription ? (
                <>
                  {
                    translationContext.translations.provetOtpDescription.split(
                      "{lastDigits}"
                    )[0]
                  }
                  <span className="font-t-semi-bold">{getLastDigits(phoneNumber)}</span>
                  {
                    translationContext.translations.provetOtpDescription.split(
                      "{lastDigits}"
                    )[1]
                  }
                </>
              ) : (
                <>
                  Enter the code sent to your phone number ending in{" "}
                  <span className="font-t-semi-bold">{getLastDigits(phoneNumber)}</span>
                </>
              )}
            </p>

            <button
              className={`mt-xxs self-start text-t-sm ${INLINE_LINK_BUTTON_CLASSES}`}
              disabled={isLoading}
              onClick={handleChangePhoneNumber}
              type="button"
              data-online-booking-action="changePhoneNumber"
            >
              {translationContext?.translations?.changePhoneNumber ||
                "Change phone number"}
            </button>
          </div>

          <OtpInput
            value={otpCode}
            onChange={handleOtpChange}
            onEnter={
              otpCode.length >= OTP_INPUT_LENGTH && !isCodeExpired
                ? handleVerify
                : undefined
            }
            disabled={isLoading}
            error={!!error}
            length={OTP_INPUT_LENGTH}
          />

          <div
            className="text-t-sm text-dark-blue"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            {!isCodeExpired ? (
              <span>
                {translationContext?.translations?.provetOtpExpires ? (
                  <>
                    {translationContext.translations.provetOtpExpires.split("{time}")[0]}
                    <span className="font-t-semi-bold">{formatTime(timeRemaining)}</span>
                    {translationContext.translations.provetOtpExpires.split("{time}")[1]}
                  </>
                ) : (
                  <>
                    Expires in:{" "}
                    <span className="font-t-semi-bold">{formatTime(timeRemaining)}</span>
                  </>
                )}
              </span>
            ) : (
              <span className="text-pink-1200" role="alert">
                {translationContext?.translations?.provetOtpErrorExpired ||
                  "Code expired. Request a new code."}
              </span>
            )}
          </div>

          {error && <ErrorMessage error={error} />}
        </div>

        <div className="flex flex-col gap-lg">
          <Button
            onClick={handleVerify}
            type={ButtonType.Button}
            color={ButtonColor.DarkBlue}
            size={ButtonSize.LG}
            isLoading={isLoading}
            disabled={otpCode.length < 6 || isLoading || isCodeExpired}
            data-online-booking-action="verifyCode"
          >
            {translationContext?.translations?.provetOtpVerifyButton || "Verify"}
          </Button>

          <div className="text-center text-t-md">
            <span className="text-t-neutral-700">
              {translationContext?.translations?.provetOtpResendPrefix ||
                "Didn't receive a code?"}{" "}
            </span>

            <button
              aria-disabled={isLoading || !canResend}
              aria-label={`Resend verification code to phone number ending in ${getLastDigits(phoneNumber)}`}
              className={INLINE_LINK_BUTTON_CLASSES}
              disabled={isLoading || !canResend}
              onClick={handleResendCode}
              type="button"
            >
              {!canResend
                ? `${translationContext?.translations?.provetOtpResendButton || "Resend code"} (${resendCooldown}s)`
                : translationContext?.translations?.provetOtpResendButton ||
                  "Resend code"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Should never reach here, but return null as fallback
  return null;
};

export default ProvetPhoneVerification;
